import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { Text } from "components/designV2/text/text";
import { usePDFViewer } from "hooks/usePDFViewer";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ColorV2 } from "config/colors";
import { LocalDate } from "@js-joda/core";
import { ViewerContainer, XmlContent } from "../salary/salary.styled";
import {
  ViewMode,
  ViewModeSwitcher,
} from "../salary/viewModeSwitcher.component";
import { ButtonsGroup, ButtonsWrapper, PdfFrame } from "./fileViewer.styled";

interface FileResponse {
  readonly data: BlobPart;
}

type FileViewerProps<TListItem> = {
  readonly currentDate?: LocalDate;
  readonly emptyState?: React.ReactNode;
  readonly extraActions?: React.ReactNode;
  readonly fetchEndpoint: () => Promise<FileResponse>;
  readonly filename: string;
  readonly ListComponent?: React.ComponentType<{
    readonly currentDate: LocalDate;
    readonly data: TListItem[];
    readonly setData: React.Dispatch<React.SetStateAction<TListItem[]>>;
  }>;
  readonly refreshKey?: number;
  readonly type: string;
};

export const FileViewer = <TListItem,>(
  props: FileViewerProps<TListItem>,
): JSX.Element => {
  const {
    viewFile,
    downloadFile,
    cleanupFile,
    fileUrl,
    fileContent,
    isLoading,
    prepareExport,
  } = usePDFViewer();
  const { t } = useTranslation();
  const [xmlContent, setXmlContent] = useState<string>("");
  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [data, setData] = useState<TListItem[]>([]);

  useEffect(() => {
    setData([]);
  }, [props.currentDate]);

  const isPdf = props.type.includes("pdf");

  useEffect(() => {
    const loadFile = async () => {
      await viewFile(props.fetchEndpoint(), props.type);
    };

    loadFile();

    return () => {
      cleanupFile();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.fetchEndpoint, props.refreshKey, props.type]);

  useEffect(() => {
    if (fileContent && !isPdf) {
      const reader = new FileReader();
      reader.onload = (e) => {
        if (typeof e.target?.result === "string") {
          setXmlContent(e.target.result);
        }
      };
      reader.readAsText(fileContent);
    }
  }, [fileContent, isPdf]);

  if (isLoading) {
    return (
      <Text typography="b2" color={ColorV2.Blue1} align="center">
        {t("General.Loading")}
      </Text>
    );
  }

  const renderButtons = (showDownloadActions: boolean): JSX.Element => {
    return (
      <ButtonsWrapper>
        {showDownloadActions && !isPdf ? (
          <ViewModeSwitcher onChange={setViewMode} defaultMode={viewMode} />
        ) : (
          <span />
        )}
        <ButtonsGroup>
          {showDownloadActions && (
            <PrimaryButton
              label={isPdf ? t("Salary.DownloadPdf") : t("Salary.DownloadXML")}
              size="small"
              onClick={() => downloadFile(props.filename)}
            />
          )}
          {showDownloadActions && !isPdf && (
            <PrimaryButton
              label={t("Salary.DownloadSIE")}
              size="small"
              onClick={() => {
                if (props.currentDate) {
                  prepareExport(
                    props.currentDate.monthValue(),
                    "SIE",
                    props.currentDate.year(),
                  );
                }
              }}
            />
          )}
          {props.extraActions}
        </ButtonsGroup>
      </ButtonsWrapper>
    );
  };

  if (!fileUrl) {
    if (props.emptyState) return <>{props.emptyState}</>;

    return (
      <>
        {props.extraActions ? renderButtons(false) : null}
        <Text typography="b2" color={ColorV2.Blue1} align="center">
          {t("Salary.NoFileToShow")}
        </Text>
      </>
    );
  }

  const showXmlOrPdf = isPdf || viewMode === "xml";

  return (
    <>
      {renderButtons(true)}

      {showXmlOrPdf ? (
        <ViewerContainer>
          {isPdf ? (
            <PdfFrame src={`${fileUrl}#toolbar=1`} title="PDF Viewer" />
          ) : (
            <XmlContent>
              <pre>{xmlContent}</pre>
            </XmlContent>
          )}
        </ViewerContainer>
      ) : props.currentDate && props.ListComponent ? (
        <props.ListComponent
          currentDate={props.currentDate}
          data={data}
          setData={setData}
        />
      ) : null}
    </>
  );
};
